import {CursorType} from '@/enums/diagrams';
import Highcharts, {EventCallbackFunction} from 'highcharts';
import {Cursor} from './cursor';
import {CursorManager} from './cursorManager';
import {CursorLabelModel} from './labelModels/cursorLabelModel';
import {uuidv4} from '@u/uuidUtils';
import {CursorData} from '../cursorDiagramState';
import {groupBy} from 'lodash';
import {MarkerSeriesPoint} from '@/interfaces/markers';
import {CursorPlotLine} from './cursorPlotLine';

/*
  Класс курсора для графика Тепловая карта
*/
export class HeatmapCursor implements Cursor {
  // Идентификатор курсора
  id: string = uuidv4();

  // Тип курсора
  type: CursorType = CursorType.Cursor;

  // Ссылка на менеджер курсоров (используется т. к. нет событий)
  cursorManager: CursorManager | null = null;

  // Модель текстового представления
  labelModel: CursorLabelModel | null;

  // Объект plotLine курсора (внутреннее состояние)
  protected cursorPlotLine!: CursorPlotLine;

  // Сохраненные данные с точки графика (нужно для серверных графиков)
  protected copiedPoint: {x: number; y: number} | null = null;

  // Ссылка на объект графика
  protected chart: any;

  // Показывать линию курсора
  protected isShowLine = true;

  // Показывать отметки курсора
  protected isShowTooltips = false;

  // Серия к которой привязан курсор
  protected bindedSeriesIds: string[] = [];

  // Показатель заморозки событий
  protected isFreezeEvents = false;

  // Серии с маркерами
  protected markerSeries: MarkerSeriesPoint[] = [];

  // Callback функция, вызываемая при применении курсора
  protected applyCallback: Function | null = null;

  // Цвет маркера и линии курсора
  color: string;

  // Отметки точки курсора
  tooltips: any[] = [];

  // Точность представления нецелых чисел
  protected viewRoundingAccuracy = 2;

  // Ширина тултипа
  protected tooltipBoundingBoxWidth = 64;

  constructor(
    labelModel: CursorLabelModel | null = null,
    applyCallback: Function | null = null,
    color = '#00FF00FF', //'rgba(0, 255, 0, 1)',
  ) {
    this.color = color;
    this.applyCallback = applyCallback;
    this.labelModel = labelModel;
  }

  /*
    Получить данные курсора
  */
  getCursorData() {
    return new CursorData(this.id, this.type, this.copiedPoint?.x, null, null, null);
  }

  /*
    Инициализация курсора
  */
  init(
    cursorManager: CursorManager,
    chart: any,
    bindedSeriesIds: string[] = [],
    isShowLine = true,
    isShowTooltips = false,
    cursorData: CursorData | null | undefined,
  ) {
    this.cursorManager = cursorManager;
    this.chart = chart;

    this.bindedSeriesIds = bindedSeriesIds;

    this.cursorPlotLine = new CursorPlotLine(this.chart, {
      id: `cursorPlotLine-${this.id}`,
      value: undefined,
      width: 3,
      color: this.color,
      zIndex: 9998,
    });

    this.addEventHandlers();
    this.update(isShowLine, isShowTooltips, cursorData);
  }

  /*
    Обновление курсора
  */
  update(isShowLine = true, isShowTooltips = false, cursorData: CursorData | null | undefined) {
    this.isShowLine = isShowLine;
    this.isShowTooltips = isShowTooltips;
    this.initCursorData(cursorData);
    //отправляем уведомление менеджеру куросров, чтобы он перерисовал окно информации
    this.cursorManager?.onCursorChanged();
  }

  /*
    Инициализация данных курсора
  */
  protected initCursorData(cursorData: CursorData | null | undefined) {
    if (!cursorData) return;
    const pointX = cursorData.pointX;
    if (pointX) {
      if (!this.copiedPoint) this.copiedPoint = {x: pointX, y: 0};
      else this.copiedPoint.x = pointX;
    } else {
      this.copiedPoint = null;
    }
    this.redrawPlotLinesAndMarkers();
  }

  /*
    Добавление обработчиков событий после прорисовки графика
    https://www.highcharts.com/docs/extending-highcharts/extending-highcharts
  */
  private bindedClickHandler: EventCallbackFunction<any> | undefined;

  private bindedMoveHandler: EventCallbackFunction<any> | undefined;
  private bindedUpHandler: EventCallbackFunction<any> | undefined;

  private addEventHandlers() {
    this.bindedClickHandler = this.onClickHandler.bind(this);
    this.bindedMoveHandler = this.onMoveHandler.bind(this);
    this.bindedUpHandler = this.onUpHandler.bind(this);

    Highcharts.addEvent(this.chart.container, 'click', this.bindedClickHandler);
    Highcharts.addEvent(this.chart.container, 'mousemove', this.bindedMoveHandler);
    Highcharts.addEvent(this.chart.container, 'mouseup', this.bindedUpHandler);
  }

  /*
    Удаление обработчиков событий
  */
  private async removeEventHandlers() {
    Highcharts.removeEvent(this.chart.container, 'click', this.bindedClickHandler);
    Highcharts.removeEvent(this.chart.container, 'mousemove', this.bindedMoveHandler);
    Highcharts.removeEvent(this.chart.container, 'mouseup', this.bindedUpHandler);

    //Очищаем сохраненные обработчики
    this.bindedClickHandler = undefined;
    this.bindedMoveHandler = undefined;
    this.bindedUpHandler = undefined;
  }

  /*
    Обработчик снятия кнопки мыши с области графика
  */
  protected onUpHandler() {
    if (this.cursorPlotLine.isDrag()) this.cursorPlotLine.stopDrag();
  }

  /*
    Обработчик перемещения мыши по области графика
  */
  protected onMoveHandler(e: any) {
    if (this.cursorPlotLine.isDrag()) {
      const newXValue = this.cursorPlotLine.translateX(e.pageX, e.chartX);
      if (!newXValue) return;

      if (this.copiedPoint) this.copiedPoint.x = newXValue;

      this.markerSeries.forEach((ms: any) => {
        ms.x = this.copiedPoint?.x;
        ms.y = this.copiedPoint?.y;
        this.drawMarkersSeries(ms.id, ms.yAxis, {x: ms.x, y: ms.y});
      });
      this.notify();
    }
  }

  /*
    Обработчик клика по пустой области графика
  */
  protected onClickHandler() {
    if (this.isFreezeEvents) return;
    const point = this.chart.hoverPoint;
    this.applyWithNotification({x: point.x, y: point.y});
  }

  /*
    Применить курсор с уведомлением о изменении
  */

  private applyWithNotification(point: {x: number; y: number}) {
    this.apply(point);
    this.notify();
  }

  /*
    Уведомление о изменении менежеру курсоров и вызов applyCallback
  */
  notify() {
    this.cursorManager?.onCursorChanged();
    if (this.applyCallback) this.applyCallback();
  }

  /*
    Действия при активации курсора
  */

  apply(point: {x: number; y: number}) {
    this.copiedPoint = {...point};
    this.redrawPlotLinesAndMarkers();
  }

  /*
    Действия при очистке курсора
  */

  clear() {
    // удаляем отметку
    this.clearTooltip();
    // удаляем с графика серии с маркерами
    this.markerSeries.forEach((series: any) => {
      this.chart.get(series.id)?.remove();
    });

    // удаляем обработчки событий
    this.removeEventHandlers();

    // удаляем линию курсора с графика
    this.cursorPlotLine.remove();

    // устанавливаем все свойства в исходное состояние
    this.copiedPoint = null;
    this.isShowLine = true;
    this.isShowTooltips = false;
    this.chart = null;
    //this.bindedSeries = [];
    this.isFreezeEvents = false;
    this.markerSeries = [];
    // отправляем менеджеру уведомление о изменении
    this.cursorManager?.onCursorChanged();
  }

  /*
   Рисуем серию с маркерами
 */
  protected drawMarkersSeries(markerSeriesId: string, axisNumber: number, point: any) {
    let markerSeries = this.chart.get(markerSeriesId);

    // имеет смысл если мы не удалем все серии
    if (!markerSeries) {
      // если серия с маркерами не найдена, то создаем ее (без перерисовки)

      markerSeries = this.chart.addSeries(
        {
          id: markerSeriesId,
          enableMouseTracking: false,
          showInLegend: false,
          type: 'scatter',
          data: [],
          lineWidth: 0,
          zIndex: 9999,
          marker: {
            symbol: 'circle',
            fillColor: this.color,
            lineColor: this.color,
            radius: 5,
            enabled: true,
            zIndex: 9999,
          },
          hover: {
            enabled: false,
            halo: {
              size: 0,
            },
            inactive: {
              enabled: false,
            },
          },
          states: {
            inactive: {
              opacity: 1,
            },
          },
          yAxis: axisNumber,
        },
        false,
      );
    }

    // добавляем точки маркеров в серию (с перерисовкой)
    markerSeries.setData([point]);
  }

  protected newMarkerSeriesId(id: string) {
    return `markerSeries-${id}`;
  }

  /*
    Перерисовать все линии и маркеры
  */
  protected redrawPlotLinesAndMarkers(redrawMarkers = true) {
    if (!this.copiedPoint) {
      //очищаем линии и маркеры
      this.cursorPlotLine.remove();
      this.markerSeries.forEach((series: any) => {
        this.chart.get(series.id)?.remove();
      });
      return;
    }

    const markerSeries: MarkerSeriesPoint[] = [];
    const extremes = this.chart.xAxis[0].getExtremes();
    if (extremes.min <= this.copiedPoint.x && extremes.max >= this.copiedPoint.x) {
      if (this.copiedPoint?.x) {
        markerSeries.push({
          chartSeriesId: 'marker',
          yAxis: 0,
          x: this.copiedPoint.x,
          y: this.copiedPoint.y,
        });
      }
    }

    // обновляем линию курсора
    if (this.isShowLine) this.cursorPlotLine.redraw(this.copiedPoint.x);
    else this.cursorPlotLine.remove();

    if (!redrawMarkers) return;

    // удаляем серии маркеров для несуществующих графиков
    this.markerSeries.forEach((series: any) => {
      const findIndex = markerSeries.findIndex(
        (newSeries: any) => newSeries.chartSeriesId === series.chartSeriesId,
      );
      if (findIndex === -1) this.chart.get(series.id)?.remove();
      else markerSeries[findIndex] = {...markerSeries[findIndex], id: series.id};
    });

    this.markerSeries = [...markerSeries];

    this.markerSeries.forEach((series: any) => {
      series.id = series.id !== undefined ? series.id : this.newMarkerSeriesId(uuidv4());
      this.drawMarkersSeries(series.id, series.yAxis, {x: series.x, y: series.y});
    });
  }

  /*
    Действия при перерисовке (любом изменении графика)
  */
  render() {
    this.redrawTooltip();
  }

  /*
    Перерисовка отметки
  */
  private redrawTooltip() {
    this.clearTooltip();

    if (!this.isShowTooltips) return;

    this.markerSeries.forEach((series: any) => {
      const t = this.drawTooltip({x: series.x, y: series.y}, series.yAxis);
      this.tooltips.push(t);
    });
  }

  /*
    Очистить отметку
  */
  private clearTooltip() {
    this.tooltips.forEach((t: any) => {
      t.destroy();
      t = undefined;
    });
    this.tooltips = [];
  }

  /*
    Отрисовать отметку на точке
  */
  protected drawTooltip(point: any, indexAxis: number) {
    const xAxis = this.chart.xAxis[0];
    const yAxis = this.chart.yAxis[indexAxis];

    const plotX = xAxis.toPixels(point.x);
    const plotY = yAxis.toPixels(point.y);

    //Обработка выхода тултипа за границы диаграммы
    let shiftY = -38;
    let shiftX = 5;
    const height = 28;

    const extremesY = yAxis.getExtremes();
    const extremesX = xAxis.getExtremes();

    //plotMinY т.к. max в координатах соответствует min в пикселях
    const plotMinY = yAxis.toPixels(extremesY.max);
    const plotMaxX = xAxis.toPixels(extremesX.max);

    if (plotY + shiftY < plotMinY) shiftY = 0;

    if (plotX + this.tooltipBoundingBoxWidth > plotMaxX)
      shiftX = -this.tooltipBoundingBoxWidth - 2 * shiftX;

    return this.chart.renderer
      .label(this.getTooltipStr(point.x, point.y), plotX + shiftX, plotY + shiftY)
      .css({
        'fontSize': '11px',
      })
      .attr({
        fill: 'rgba(255, 230, 176, 0.9)',
        stroke: 'black',
        'stroke-width': 1,
        padding: 2,
        paddingLeft: 2,
        paddingRight: 2,
        height: height,
        r: 2,
        zIndex: 10001,
      })
      .add();
  }

  /*
    Получить строку для отображения в тултипе
  */
  protected getTooltipStr(x: number, y: number) {
    let formatX = x.toFixed(this.viewRoundingAccuracy);
    if (this.labelModel && this.labelModel.xValueFormatter)
      formatX = this.labelModel.xValueFormatter(x);
    const formatY = y.toFixed(this.viewRoundingAccuracy);
    return `X=${formatX}<br/>Y=${formatY}`;
  }

  /*
    Установка заморозки событий
  */
  freezeEvents(freeze: boolean) {
    this.isFreezeEvents = freeze;
    this.cursorPlotLine.freezeEvents(freeze);
  }

  /*
    Переключение показа линии
  */
  showLine(show: boolean) {
    this.isShowLine = show;
    this.redrawPlotLinesAndMarkers(false);
  }

  /*
    Переключение показа отметок
  */
  showTooltips(show: boolean) {
    this.isShowTooltips = show;
    this.redrawTooltip();
  }

  /*
    Показатель того, что курсор применен
  */
  isApplied() {
    return this.copiedPoint != null; //check null or undefined
  }

  /*
    Получение строки курсора
  */
  public getLabelStr() {
    if (!this.copiedPoint) return '';
    let xValue = this.copiedPoint.x.toFixed(this.viewRoundingAccuracy);
    if (this.labelModel && this.labelModel.xValueFormatter)
      xValue = this.labelModel.xValueFormatter(this.copiedPoint.x);

    let yValuesStr = '';
    const groupedSeries = groupBy(this.markerSeries, 'yAxis');
    for (const yAxis in groupedSeries) {
      const yValues = groupedSeries[yAxis]
        .map((series: any) => (series.y ? series.y.toFixed(this.viewRoundingAccuracy) : ''))
        .join(', ');
      const axisNumber = groupedSeries[yAxis][0].yAxis;
      yValuesStr = `${yValuesStr}, ${this.chart.options.yAxis[axisNumber].title.text}:${yValues}`;
    }

    return `${this.chart.options.xAxis[0].title.text}:${xValue}${yValuesStr}`;
  }
}
